import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Shield, Info, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useCookieConsentContext } from '@/contexts/CookieConsentContext';

export function CookiePreferencesModal() {
  const { 
    showPreferencesModal, 
    closePreferences, 
    savePreferences, 
    acceptAll,
    consentState 
  } = useCookieConsentContext();
  
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Sync with current preferences when modal opens
  useEffect(() => {
    if (showPreferencesModal && consentState?.preferences) {
      setAnalyticsEnabled(consentState.preferences.analytics ?? false);
    }
  }, [showPreferencesModal, consentState?.preferences?.analytics]);

  const handleSave = () => {
    savePreferences({
      necessary: true,
      analytics: analyticsEnabled,
    });
  };

  const handleAcceptAll = () => {
    setAnalyticsEnabled(true);
    acceptAll();
  };

  return (
    <AnimatePresence>
      {showPreferencesModal && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            onClick={closePreferences}
            aria-hidden="true"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-50 max-w-lg w-full max-h-[90vh] overflow-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="preferences-modal-title"
          >
            <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center">
                    <Cookie className="w-5 h-5 text-orange-400" />
                  </div>
                  <h2 id="preferences-modal-title" className="text-xl font-semibold text-white">
                    Cookie Preferences
                  </h2>
                </div>
                <button
                  onClick={closePreferences}
                  className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  aria-label="Close preferences"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                <p className="text-sm text-slate-300">
                  Manage your cookie preferences below. Essential cookies are always active as they're required for the website to function. See our{' '}
                  <Link 
                    to="/privacy" 
                    className="text-orange-400 hover:text-orange-300 underline underline-offset-2"
                    onClick={closePreferences}
                  >
                    Privacy Policy
                  </Link>{' '}
                  for more details.
                </p>

                {/* Cookie Categories */}
                <div className="space-y-4">
                  {/* Necessary Cookies - Always On */}
                  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-white">Necessary Cookies</h3>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                            <Check className="w-3 h-3" />
                            Always Active
                          </span>
                        </div>
                        <p className="text-sm text-slate-400">
                          Required for basic site functionality, security, and remembering your preferences. These cannot be disabled.
                        </p>
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        <div className="w-11 h-6 rounded-full bg-cyan-500 flex items-center justify-end px-1 cursor-not-allowed opacity-60">
                          <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies - Toggle */}
                  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-white">Analytics Cookies</h3>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
                            <Info className="w-3 h-3" />
                            Optional
                          </span>
                        </div>
                        <p className="text-sm text-slate-400">
                          Help us understand how visitors interact with our website. Data is anonymised and used solely to improve site performance and user experience. Powered by Google Analytics.
                        </p>
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        <Switch
                          checked={analyticsEnabled}
                          onCheckedChange={setAnalyticsEnabled}
                          aria-label="Toggle analytics cookies"
                          className="data-[state=checked]:bg-orange-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust indicator */}
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/30 border border-slate-700/50">
                  <Shield className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  <span className="text-xs text-slate-400">
                    UK GDPR & PECR Compliant. You can change your preferences at any time via the Cookie Settings link in the footer.
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-slate-700 bg-slate-800/30">
                <Button
                  onClick={handleSave}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white min-h-[44px]"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white shadow-lg shadow-orange-500/25 min-h-[44px]"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
